import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectActiveCompany,
  selectDocumentsFilterAuthPerson,
  selectDocumentsFilterBusiness,
  selectDocumentsFilterDirector,
  selectDocumentsFilterRejected,
  selectIsDocumentsLoading,
  selectIsTraderootDown,
  selectLatestProductsStatusInfo,
  selectPrimaryUser,
} from '@features/workspace/store/selectors';

import { CompanyTypeEnum, ProductTypeEnum } from '@utils/constants';

import Layout from '@features/workspace/components/layouts/v1/layout';
import { DocumentsTable } from '@features/workspace/views/document/components/documents-table';
import { CollapseGroup } from '@features/workspace/components/shared/collapse-group';
import { setIsLoading } from '@store/features/loading';
import ConfirmDialog from '@features/workspace/components/dialogs/confirm-dialog';
import { setNotification } from '@store/features/notification';
import { blockUnblockUser } from '@features/workspace/store/actions';
import { ActionLinkEdit } from '@features/workspace/components/controls/action-link';
import UploadDocumentsModal from '@features/workspace/views/document/modals/upload-documents-modal';
import useApprove, {
  IJobInfo,
  JobIdEnum,
  JobStatusEnum,
} from '@features/workspace/hooks/use-approve';
import {
  CheckCircleOutline,
  ErrorOutline,
  RadioButtonUncheckedOutlined,
  RestartAlt,
} from '@mui/icons-material';
import { Loading } from '@features/workspace/components/layouts/v2/loading';

const DOCUMENT_TYPE = {
  BUSINESS: 'business',
  BANK_STATEMENT: 'bank_statement',
};

const DocumentPage: React.FC<any> = () => {
  useEffect(() => {
    document.title = 'Lula Support - Documents';
  }, []) //eslint-disable-line

  const dispatch = useAppDispatch();

  const { approve, isApproved, isApproveInProgress, jobsInfo, execJob } =
    useApprove();

  const approveJobsGrouped: IJobInfo[][] = useMemo(
    () => [
      [
        jobsInfo[JobIdEnum.CreateTraderootMember],
        jobsInfo[JobIdEnum.CreateBankAccount],
      ],
    ],
    [jobsInfo]
  );

  const [openAcceptConfirmationDialog, setAcceptConfirmationDialog] =
    useState(false);
  const [openRejectConfirmationDialog, setRejectConfirmationDialog] =
    useState(false);

  const primaryUser = useAppSelector(selectPrimaryUser);
  const [openUploadDocumentsType, setOpenUploadDocumentsType] = useState<
    null | string
  >(null);

  const isTraderootDown = useAppSelector(selectIsTraderootDown);
  const businessDocuments = useAppSelector(selectDocumentsFilterBusiness);
  const directorsDocuments = useAppSelector(selectDocumentsFilterDirector);
  const authPersonsDocuments = useAppSelector(selectDocumentsFilterAuthPerson);
  const rejectedDocuments = useAppSelector(selectDocumentsFilterRejected);
  const latestProducts = useAppSelector(selectLatestProductsStatusInfo);

  const company = useAppSelector(selectActiveCompany);
  const isDocumentsLoading = useAppSelector(selectIsDocumentsLoading);

  const incompleteCompanyErrMessage = useMemo(() => {
    if (company) {
      if (isTraderootDown) {
        return 'Traderoot is down. Please try again later.';
      }
      if (
        [CompanyTypeEnum.ClosedCorporation, CompanyTypeEnum.Private].includes(
          company.companyType
        ) &&
        !company.registrationNumber &&
        !company.taxNumber
      ) {
        return 'Company Registration Number and Tax Number required';
      }
      if (
        [CompanyTypeEnum.ClosedCorporation, CompanyTypeEnum.Private].includes(
          company.companyType
        ) &&
        !company.registrationNumber
      ) {
        return 'Company Registration Number required';
      }
      if (
        [
          CompanyTypeEnum.SoleTrader,
          CompanyTypeEnum.ClosedCorporation,
          CompanyTypeEnum.Private,
        ].includes(company.companyType) &&
        !company.taxNumber
      ) {
        return 'Tax Number required';
      }
    }

    return null;
  }, [company]);

  const bankingProduct = useMemo(
    () =>
      latestProducts.find(
        (p) => p.latestProduct?.type === ProductTypeEnum.Banking
      ),
    [latestProducts]
  );

  const hasBankingProduct = useMemo(
    () => bankingProduct !== undefined,
    [bankingProduct]
  );

  const documentsCollection: Array<any> = [
    { title: '', items: businessDocuments },
    { title: 'Directors', items: directorsDocuments },
    { title: 'Authorised Persons', items: authPersonsDocuments },
  ];

  const totalBusinessDocuments = documentsCollection.reduce(
    (sum, current) => sum + current.items.length,
    0
  );

  const rejectedDocumentsCollection: Array<any> = [
    { title: '', items: rejectedDocuments },
  ];

  const onConfirmAccountActivation = async () => {
    setAcceptConfirmationDialog(true);
  };

  const onConfirmAccountApplicationRejection = async () => {
    setRejectConfirmationDialog(true);
  };

  const onDeclineAccountActivation = async () => {
    dispatch(setIsLoading(true));
    setRejectConfirmationDialog(false);

    await dispatch(blockUnblockUser({ email: primaryUser.id }));
    dispatch(
      setNotification({
        message: `Bank account request declined - ${primaryUser.email} blacklisted!`,
        variant: 'warning',
      })
    );

    dispatch(setIsLoading(false));
  };

  useEffect(() => {
    if (isApproved) {
      dispatch(setIsLoading(false));
    }
  }, [isApproved, dispatch]);

  const onActivateAccount = async () => {
    setAcceptConfirmationDialog(false);
    approve();
  };

  const onCancel = async () => {
    setAcceptConfirmationDialog(false);
    setRejectConfirmationDialog(false);
  };

  const onOpenUploadDocuments = (documentType: string) => {
    setOpenUploadDocumentsType(documentType);
  };

  const onCloseUploadDocuments = () => {
    setOpenUploadDocumentsType(null);
  };

  const onSaveUploadDocuments = (data: any) => {
    setOpenUploadDocumentsType(null);
  };

  const getListItemIcon = (job: IJobInfo) => {
    switch (job.status) {
      case JobStatusEnum.Passed:
        return <CheckCircleOutline />;
      case JobStatusEnum.Failed:
        return (
          <Box>
            <ErrorOutline />
            <RestartAlt cursor="pointer" onClick={() => execJob(job.id)} />
          </Box>
        );
      case JobStatusEnum.Pending:
        return <CircularProgress size={20} style={{ color: '#0000008a' }} />;
      default:
        return <RadioButtonUncheckedOutlined />;
    }
  };

  return (
    <>
      <Layout>
        <Card>
          <CardContent>
            <CollapseGroup
              expandByDefault
              button={
                <Box
                  alignItems="center"
                  display="flex"
                  flexDirection="row"
                  width="100%"
                >
                  <Box minWidth="200px" ml={3}>
                    <ActionLinkEdit uri="/customer/company/edit#documents">
                      Edit company information
                    </ActionLinkEdit>
                  </Box>
                  <Box
                    display="flex"
                    flexGrow={1}
                    justifyContent="flex-end"
                    pr={2}
                  >
                    <Button
                      variant="outlined"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenUploadDocuments(DOCUMENT_TYPE.BUSINESS);
                      }}
                    >
                      Upload documents
                    </Button>
                  </Box>
                </Box>
              }
              counter={totalBusinessDocuments}
              header="Business Documents"
              id="businessDocuments"
              isCounterLoading={isDocumentsLoading}
            >
              {isDocumentsLoading && <Loading />}
              {!isDocumentsLoading &&
                documentsCollection.map((collection: any) => (
                  <DocumentsTable
                    key={`documents-business-${collection.title}`}
                    documents={collection.items}
                    hasBankingProduct={hasBankingProduct}
                    title={collection.title}
                  />
                ))}
            </CollapseGroup>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <CollapseGroup
              counter={rejectedDocuments.length}
              header="Archived Documents"
              id="rejectedDocuments"
            >
              {isDocumentsLoading && <Loading />}
              {!isDocumentsLoading &&
                rejectedDocumentsCollection.map((collection: any) => (
                  <DocumentsTable
                    key={`documents-rejected-${collection.title}`}
                    rejectedMode
                    documents={collection.items}
                    hasBankingProduct={hasBankingProduct}
                    noItemsMessage="No archived documents"
                    title={collection.title}
                  />
                ))}
            </CollapseGroup>
          </CardContent>
        </Card>

        {/* Only show activate account button if requesting bank account */}
        {!!incompleteCompanyErrMessage && (
          <Box display="flex" flexDirection="column" my={3}>
            <Box display="flex" justifyContent="flex-end">
              <Typography color="#f44336" variant="subtitle2">
                {incompleteCompanyErrMessage}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <ActionLinkEdit uri="/customer/company/edit#documents">
                Edit company information
              </ActionLinkEdit>
            </Box>
          </Box>
        )}
        {!isTraderootDown && (
          <Box display="flex" justifyContent="flex-end">
            {approveJobsGrouped.map((group: IJobInfo[], iGroup: number) => (
              <Box key={`approve-job-group-${iGroup}`} display="flex">
                <List>
                  {group.map((job) => (
                    <ListItem key={`approve-job-${job.id}`}>
                      <ListItemIcon>{getListItemIcon(job)}</ListItemIcon>
                      <ListItemText>{job.title}</ListItemText>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
        {!isApproved && !isDocumentsLoading && !isTraderootDown && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Box display="flex" gap={1}>
              <Button
                disabled={isApproveInProgress}
                size="large"
                onClick={onConfirmAccountApplicationRejection}
              >
                Decline banking application
              </Button>

              <Button
                autoFocus
                disabled={!!incompleteCompanyErrMessage || isApproveInProgress}
                size="large"
                variant="contained"
                onClick={onConfirmAccountActivation}
              >
                Create bank account
              </Button>
            </Box>
          </Box>
        )}

        <ConfirmDialog
          open={openAcceptConfirmationDialog}
          title="Are you sure you want to create this customer's bank account?"
          onCancel={onCancel}
          onClose={onCancel}
          onConfirm={onActivateAccount}
        />

        <ConfirmDialog
          open={openRejectConfirmationDialog}
          title="Are you sure you want to reject this customer's bank account application so that he will be blacklisted?"
          onCancel={onCancel}
          onClose={onCancel}
          onConfirm={onDeclineAccountActivation}
        />

        <UploadDocumentsModal
          open={!!openUploadDocumentsType}
          onClose={onCloseUploadDocuments}
          onSave={onSaveUploadDocuments}
        />
      </Layout>
    </>
  );
};

export default DocumentPage;
